// <s-cart-addon>: one-tap add for a single cross-sell item (the "Complete your setup" row in the cart
// drawer and cart page, and the offer inside the add-to-cart pop-up).
// Attributes (written by snippets/cart-addon.liquid):
//   data-variant-id    — variant to add (quantity 1)
//   data-cart-add-url  — routes.cart_add_url, used for the Ajax fallback
//   data-error-message — shown in [data-addon-error] when the add fails
//   data-open-cart      — close the surrounding dialog and open the cart drawer after adding
//   data-checkout-after — present in the pop-up: go straight to checkout after adding (submits form[data-addon-checkout])
// Uses Shopify Standard Actions so the cart partials refresh (which hides this offer once it's in the cart).
class SCartAddon extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.querySelector('[data-addon-add]')?.addEventListener('click', () => this.#add(), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #add() {
    if (this.getAttribute('aria-busy') === 'true') return;
    const variantId = String(this.dataset.variantId || '').trim();
    if (!variantId) return;

    const dialog = this.closest('dialog');
    const openCartAfter = this.hasAttribute('data-open-cart');
    const checkoutForm = this.hasAttribute('data-checkout-after') ? this.querySelector('form[data-addon-checkout]') : null;
    this.#setBusy(true);
    this.#showError('');

    let added = false;
    let message = '';
    try {
      const actions = window.Shopify?.actions;
      if (actions?.updateCart) {
        try {
          const result = await actions.updateCart(
            { lines: [{ merchandiseId: variantId, quantity: 1 }] },
            { event: { context: 'product', detail: { source: 's-cart-addon' } } }
          );
          message = result?.userErrors?.[0]?.message || '';
          added = !message;
        } catch {
          // Fall through to the Ajax cart below.
        }
      }
      if (!added) added = await this.#ajaxAdd(variantId);
    } finally {
      this.#setBusy(false);
    }

    if (!added) {
      this.#showError(message || this.dataset.errorMessage || '');
      return;
    }

    if (checkoutForm instanceof HTMLFormElement) {
      // Pop-up: the shopper chose the bundle, so take them straight to checkout.
      checkoutForm.submit();
      return;
    }

    if (openCartAfter) {
      dialog?.close();
      await SCartAddon.openCart();
    }
  }

  async #ajaxAdd(variantId) {
    const url = this.dataset.cartAddUrl;
    if (!url) return false;
    try {
      const response = await fetch(`${url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!response.ok) return false;
      await customElements.get('s-cart')?.refreshMountedCarts?.();
      return true;
    } catch {
      return false;
    }
  }

  static async openCart() {
    const actions = window.Shopify?.actions;
    if (actions?.openCart) {
      await actions.openCart();
      return;
    }
    const drawer = document.getElementById('cart-drawer');
    if (drawer instanceof HTMLDialogElement && !drawer.open) drawer.showModal();
  }

  #setBusy(busy) {
    const button = this.querySelector('[data-addon-add]');
    if (busy) this.setAttribute('aria-busy', 'true');
    else this.removeAttribute('aria-busy');
    if (button instanceof HTMLButtonElement) {
      button.disabled = busy;
      button.classList.toggle('opacity-60', busy);
    }
  }

  #showError(text) {
    const error = this.querySelector('[data-addon-error]');
    if (!error) return;
    error.textContent = text;
    error.hidden = !text;
  }
}

if (!customElements.get('s-cart-addon')) customElements.define('s-cart-addon', SCartAddon);
